import React, { useEffect, useReducer, useState } from 'react';
import { BreadCrumb, FooterSection } from '../components';
import { TrainClasses } from '../route_info/TrainClasses';
import { CancelledTrainPageData, CancelledTrainType } from '../types';
import FullyCancelled from '../components/FullyCancelled';
import PartiallyCancelled from '../components/PartiallyCancelled';
import { FixedSizeList } from 'react-window';
import '../../styles/UpdateCenterCommon.css';
import PropTypes from 'prop-types';
import { isMobile } from '../../utils/commonUtils';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import HtmlHeader from '../components/HtmlHeader';
import { FOOTER_DATA_IRCTC, IRCTC_EXTERNAL_JS } from '../../utils/RailsSEOConstants';
import { getStaticPageLoadEvent } from '../../utils/omnitureUtils';

type CancelledTrainPageDataProps = {
  data: CancelledTrainPageData;
};
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);
const CancelledTrains = ({ data }: CancelledTrainPageDataProps) => {
  const [itemHeight, setItemHeight] = useState(70);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFully, setShowFully] = useState(true);
  const [listToRender, setListToRender] = useState<JSX.Element[]>([]);
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);

  const onRadioChanged = (e) => {
    if (e.target && e.target.id === 'fullCancel') {
      setShowFully(true);
    } else if (e.target && e.target.id === 'partiallyCancel') {
      setShowFully(false);
    }
  };

  const getRenderedList = () => {
    if (typeof window !== 'undefined' && data.cancelledTrainList) {
      const tempArr = data.cancelledTrainList.filter(
        (train, idx) =>
          train.trainName.toLowerCase().includes(searchQuery) ||
          train.trainNo.toLowerCase().includes(searchQuery) ||
          train.startDate.toLowerCase().includes(searchQuery)
      );
      const fullCancelled = tempArr.filter((train) => !train.isPartial);
      const partialCancelled = tempArr.filter((train) => train.isPartial);
      const updatedArr: JSX.Element[] = [];
      if (showFully) {
        fullCancelled.forEach((train) => {
          updatedArr.push(<FullyCancelled {...train} />);
        });
      } else if (!showFully) {
        partialCancelled.forEach((train) => {
          updatedArr.push(<PartiallyCancelled {...train} />);
        });
      }
      if (isMobile() && !showFully) {
        setItemHeight(125);
      } else if (isMobile() && showFully) {
        setItemHeight(85);
      } else {
        setItemHeight(70);
      }
      setListToRender([...updatedArr]);
    }
  };

  useEffect(() => {
    getRenderedList();
  }, [searchQuery, showFully]);

  const Row = ({ index, style }) => <div style={style}>{listToRender[index]}</div>;
  Row.propTypes = {
    index: PropTypes.number,
    style: PropTypes.object
  };
  const ListComponent = () => {
    const height = listToRender.length < 10 ? itemHeight * listToRender.length : 500;
    return (
      <FixedSizeList
        height={height}
        width="100%"
        itemSize={itemHeight}
        itemCount={listToRender.length}
        className={'railTile'}
      >
        {Row}
      </FixedSizeList>
    );
  };
  return (
    <div>
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={false}
          topFix={true}
          classNode={TrainClasses.FC}
          pageWiseContext={SearchWidgetContext}
        />
      </SearchWidgetContext.Provider>
      <div className="container">
        <HtmlHeader data={data} />
        <BreadCrumb breadcrumb={data.template.breadcrumb} />

        <div className="heading">
          <h1 className="appendTop15 appendBottom15 font22">{data.template.h1_title}</h1>
        </div>
        <div className="rescheduledWrap">
          <div className="widgetWrap">
            <div className="widgetInner">
              <div className="searchWrap">
                <span className="searchTitle">SEARCH</span>
                <input
                  type="text"
                  placeholder="By Train Name/ Number or Date"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                />
              </div>
              <ul className="caneclledWrap">
                <li className={`appendRight10 `}>
                  <input type="radio" id="fullCancel" name="radio" checked={showFully} onClick={onRadioChanged} />
                  <label htmlFor="fullCancel">Fully Cancelled</label>
                </li>
                <li className={`appendLeft10 `}>
                  <input type="radio" id="partiallyCancel" checked={!showFully} name="radio" onClick={onRadioChanged} />
                  <label htmlFor="partiallyCancel">Partially Cancelled</label>
                </li>
              </ul>
            </div>
          </div>

          <main className="container">{ListComponent()}</main>
        </div>
      </div>
      <FooterSection template={data.template} />
    </div>
  );
};

CancelledTrains.fetchData = async () => {
  const cancelledTrainList: CancelledTrainType[] = [];
  const allCancelledTrainsResponse = await fetch('https://rails-junction.redbus.in/apis/v1/cancelled_trains', {
    method: 'GET',
    headers: { 'X-CLIENT-NAME': 'junction' }
  });
  const allCancelledTrains = await allCancelledTrainsResponse.json();

  if (allCancelledTrains.data) {
    Object.keys(allCancelledTrains.data).forEach((dateKey) => {
      const train = allCancelledTrains.data[dateKey];
      const cancelledTrain = {
        _id: train._id,
        trainNo: train.train_code,
        startDate: train.start_date,
        trainName: train.train_name,
        trainSrc: train.source_name,
        trainDstn: train.destination_name,
        trainType: train.trainType,
        isPartial: train.is_partial,
        cncldFrmStn: train.cancelled_from,
        cncldToStn: train.cancelled_to,
        ct: train.ct
      };
      cancelledTrainList.push(cancelledTrain);
    });
  }
  const template = {
    template_name: `route`,
    pageUrl: `https://www.makemytrip.com/railways/cancelled-trains.html`,
    meta_title: `Check Indian Railways Cancelled Trains, IRCTC Cancelled Trains`,
    meta_description: `Check India railways cancelled trains at MakeMytrip.com. Check the list of all cancelled trains due to fog or operational reasons.`,
    meta_keywords: `cancelled train, cancel train, cancelled train list, Indian railways cancelled trains list`,
    analytics: getStaticPageLoadEvent('seo:rails:cancelled:trains'),
    footer_data: FOOTER_DATA_IRCTC,
    h1_title: `Indian Railways Cancelled Trains`,
    breadcrumb: [
      {
        redirectionUrl: `https://www.makemytrip.com/`,
        displayString: `Home`
      },
      {
        redirectionUrl: `https://www.makemytrip.com/railways/`,
        displayString: `Rails`
      },
      {
        displayString: `Cancelled Trains`,
        redirectionUrl: `https://www.makemytrip.com/railways/cancelled-trains.html`
      }
    ],
    banner_desktop: ``,
    banner_mobile: ``,
    sample_url: `https://www.makemytrip.com/railways/cancelled-trains.html`,
    external_js: IRCTC_EXTERNAL_JS,
    extra_info: ``
  };

  const finalResponse = {
    template,
    cancelledTrainList
  };
  const pageType = 'trains';
  const cacheKey = 'HTML_STATIC_CANCELLED-TRAINS.HTML';
  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    pageType,
    ...finalResponse
  };
};

export default CancelledTrains;
